/**
 * UserNotInWorkspaceError — typed error mapped from leadbilling's `AuthRole`
 * middleware response (HTTP 403, `errors.code === 'user_not_in_workspace'`).
 *
 * Surfaced when the JWT identifies a user who is not a member of the target
 * workspace (`workspace.users[]`). Usually reflects a stale session or a
 * race after a workspace membership change. Caught in `ConfirmPurchaseModal`
 * to prompt the user to sign out and back in while keeping the modal open.
 *
 * Aligns with the repo's `tClassifiedError` pattern (see
 * `NoPaymentMethodError`): an `Error` carrying a stable `code` discriminator
 * that consumers detect via `instanceof` (preferred) or duck-type on
 * `error.code`.
 */
export class UserNotInWorkspaceError extends Error {
  /** Discriminator matching the backend `errors.code` machine code. */
  readonly code = 'user_not_in_workspace' as const;

  constructor(message = 'Caller is not a member of the target workspace.') {
    super(message);
    this.name = 'UserNotInWorkspaceError';
    // Restore prototype chain after extending built-in Error (TS target ES5+ safety).
    Object.setPrototypeOf(this, UserNotInWorkspaceError.prototype);
  }
}

/** Type-guard for non-instanceof contexts (e.g. structured-clone boundaries). */
export const isUserNotInWorkspaceError = (
  err: unknown,
): err is UserNotInWorkspaceError =>
  err instanceof UserNotInWorkspaceError ||
  (err instanceof Error &&
    'code' in err &&
    (err as { code?: unknown }).code === 'user_not_in_workspace');
